<template>
  <component :is="inline === true ? 'div' : 'dialog'" :open="inline === true ? undefined : visible === true" :class="inline === true ? 'border border-base-300 rounded-box mt-4' : 'modal modal-bottom sm:modal-middle'">
    <div :class="inline === true ? 'flex flex-col' : 'modal-box max-w-5xl flex flex-col max-h-[90vh] p-0'">
      <div class="shrink-0 px-6 pt-6 pb-4 border-b border-base-300">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h3 class="font-bold text-lg">{{ t('aiChatAgentSetup.advanced.retestFineTuningModalTitle', 'Test Again And Color Status') }}</h3>
            <p class="text-sm text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.retestFineTuningModalDescription', 'The agent answers each Excel question three times, compares every answer with the human-correct answer, and colors the output workbook.') }}</p>
          </div>
          <div v-if="isRunning === true || inline !== true" class="flex items-center">
            <button v-if="isRunning === true" type="button" class="btn btn-error btn-sm mr-2" :disabled="isCancelling === true" @click="emit('cancel')">{{ isCancelling === true ? t('aiChatAgentSetup.advanced.retestFineTuningCancelling', 'Cancelling...') : t('aiChatAgentSetup.advanced.retestFineTuningCancel', 'Cancel test') }}</button>
            <button v-if="inline !== true" type="button" class="btn btn-sm btn-circle btn-ghost" :disabled="isRunning === true" @click="emit('close')">x</button>
          </div>
        </div>

        <div v-if="job !== null">
          <div class="flex items-center justify-between mb-2 text-sm">
            <span class="font-medium">{{ stageLabel }}</span>
            <span>{{ progressPercent }}%</span>
          </div>
          <progress class="progress progress-success w-full" :value="progressPercent" max="100"></progress>
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        <div v-if="error.length > 0" class="alert alert-error text-sm mb-4">
          {{ error }}
        </div>

        <div v-if="job !== null" class="grid grid-cols-1 md:grid-cols-4 mb-4">
          <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
            <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.retestFineTuningQuestions', 'Questions') }}</div>
            <div class="text-xl font-semibold">{{ processedQuestions }} / {{ totalQuestions }}</div>
          </div>
          <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
            <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.retestFineTuningTests', 'Tests') }}</div>
            <div class="text-xl font-semibold">{{ testResults.length }}</div>
          </div>
          <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
            <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.retestFineTuningFailed', 'Failed questions') }}</div>
            <div class="text-xl font-semibold">{{ failedQuestions }}</div>
          </div>
          <div class="p-3 bg-base-200 rounded-box mb-3 md:mb-0">
            <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.retestFineTuningAttempt', 'Current answer check') }}</div>
            <div class="text-xl font-semibold">{{ currentAttemptLabel }}</div>
          </div>
        </div>

        <div v-if="job !== null && currentQuestion.length > 0" class="mb-4">
          <div class="text-sm font-medium mb-1">{{ t('aiChatAgentSetup.advanced.retestFineTuningCurrentQuestion', 'Current question') }} {{ currentQuestionPosition }}</div>
          <div class="p-3 bg-white border border-base-300 rounded-box text-sm whitespace-pre-wrap">{{ currentQuestion }}</div>
        </div>

        <div v-if="visibleTestResults.length > 0" class="mb-4">
          <div class="text-sm font-medium mb-2">{{ t('aiChatAgentSetup.advanced.retestFineTuningRecordedTests', 'Recorded tests') }}</div>
          <div v-for="test in visibleTestResults" :key="test.key" class="p-3 bg-base-200 rounded-box text-sm mb-2">
            <div class="flex items-start justify-between mb-2">
              <div class="font-medium">{{ test.questionLabel }}</div>
              <span class="badge ml-3" :class="test.passed === true ? 'badge-success' : 'badge-error'">{{ test.passed === true ? t('common.pass', 'Pass') : t('common.fail', 'Fail') }}</span>
            </div>
            <div class="text-xs text-gray-500 mb-1">{{ test.name }}</div>
            <div v-if="test.reason.length > 0" class="text-xs text-gray-500 whitespace-pre-wrap">{{ test.reason }}</div>
          </div>
        </div>

        <div v-if="visibleRowResults.length > 0" class="mb-4">
          <div class="text-sm font-medium mb-2">{{ t('aiChatAgentSetup.advanced.retestFineTuningQuestionResults', 'Question results') }}</div>
          <div v-for="row in visibleRowResults" :key="row.key" class="p-3 bg-white border border-base-300 rounded-box text-sm mb-2">
            <div class="flex items-start justify-between mb-2">
              <div class="font-medium">{{ row.questionLabel }}</div>
              <span class="badge ml-3" :class="row.passed === true ? 'badge-success' : 'badge-error'">{{ row.passed === true ? t('common.pass', 'Pass') : t('common.fail', 'Fail') }}</span>
            </div>
            <div v-if="row.reason.length > 0" class="text-xs text-gray-500 whitespace-pre-wrap">{{ row.reason }}</div>
          </div>
        </div>
      </div>

      <div v-if="inline !== true" class="shrink-0 px-6 pb-6 pt-2 border-t border-base-300 modal-action mt-0">
        <button v-if="canDownload === true" type="button" class="btn mr-3" @click="emit('download')">
          {{ t('aiChatAgentSetup.advanced.retestFineTuningDownloadWorkbook', 'Download colored workbook') }}
        </button>
        <button type="button" class="btn" :disabled="isRunning === true" @click="emit('close')">
          {{ isRunning === true ? t('aiChatAgentSetup.advanced.retestFineTuningWorking', 'Working...') : t('aiChatAgentSetup.buttons.close', 'Close') }}
        </button>
      </div>
    </div>
    <form v-if="inline !== true" method="dialog" class="modal-backdrop bg-black bg-opacity-50" @click.prevent.stop="onBackdropClick">
      <button>close</button>
    </form>
  </component>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from '@/i18n';

const props = defineProps({
  visible: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  job: { type: Object, default: null },
  isRunning: { type: Boolean, default: false },
  isCancelling: { type: Boolean, default: false },
  error: { type: String, default: '' }
});

const emit = defineEmits(['close', 'download', 'cancel']);
const { t } = useI18n();

const totalQuestions = computed(() => {
  const value = Number(props.job?.totalQuestions);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const processedQuestions = computed(() => {
  const value = Number(props.job?.processedQuestions);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const failedQuestions = computed(() => {
  const value = Number(props.job?.failedQuestions);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const currentAttempt = computed(() => {
  const value = Number(props.job?.currentAttempt);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const currentAttemptLabel = computed(() => {
  if (currentAttempt.value <= 0) {
    return '-';
  }
  return `${currentAttempt.value} / 3`;
});

const progressPercent = computed(() => {
  if (totalQuestions.value <= 0) {
    return props.job?.status === 'completed' ? 100 : 0;
  }
  const value = Math.round((processedQuestions.value / totalQuestions.value) * 100);
  if (value > 100) {
    return 100;
  }
  if (value < 0) {
    return 0;
  }
  return value;
});

const currentQuestion = computed(() => typeof props.job?.currentQuestion === 'string' ? props.job.currentQuestion : '');
const currentQuestionPosition = computed(() => {
  if (totalQuestions.value <= 0) {
    return '';
  }
  if (currentQuestion.value.length === 0) {
    return '';
  }
  let number = processedQuestions.value + 1;
  if (number > totalQuestions.value) {
    number = totalQuestions.value;
  }
  return `${number} / ${totalQuestions.value}`;
});
const testResults = computed(() => Array.isArray(props.job?.testResults) === true ? props.job.testResults : []);
const rowResults = computed(() => Array.isArray(props.job?.rowResults) === true ? props.job.rowResults : []);
const canDownload = computed(() => props.job !== null && props.job !== undefined && props.job.status === 'completed');

const visibleTestResults = computed(() => {
  const source = testResults.value;
  const startIndex = source.length > 200 ? source.length - 200 : 0;
  const items = [];
  for (let index = source.length - 1; index >= startIndex; index = index - 1) {
    const item = source[index];
    const rowNumber = Number(item?.rowNumber);
    const rowLabel = Number.isFinite(rowNumber) === true && rowNumber > 0 ? `Row ${Math.floor(rowNumber)}` : t('aiChatAgentSetup.advanced.retestFineTuningUnknownRow', 'Row');
    const question = typeof item?.question === 'string' && item.question.length > 0 ? item.question : t('aiChatAgentSetup.advanced.retestFineTuningUnknownQuestion', 'Question');
    const name = typeof item?.name === 'string' && item.name.length > 0 ? item.name : t('aiChatAgentSetup.advanced.retestFineTuningTest', 'Test');
    items.push({
      key: `${index}-${rowLabel}-${name}`,
      questionLabel: `${rowLabel}: ${question}`,
      name,
      passed: item?.passed === true,
      reason: typeof item?.reason === 'string' ? item.reason : ''
    });
  }
  return items;
});

const visibleRowResults = computed(() => {
  const source = rowResults.value;
  const startIndex = source.length > 100 ? source.length - 100 : 0;
  const items = [];
  for (let index = source.length - 1; index >= startIndex; index = index - 1) {
    const item = source[index];
    const rowNumber = Number(item?.rowNumber);
    const rowLabel = Number.isFinite(rowNumber) === true && rowNumber > 0 ? `Row ${Math.floor(rowNumber)}` : t('aiChatAgentSetup.advanced.retestFineTuningUnknownRow', 'Row');
    const question = typeof item?.question === 'string' && item.question.length > 0 ? item.question : t('aiChatAgentSetup.advanced.retestFineTuningUnknownQuestion', 'Question');
    const result = typeof item?.result === 'string' ? item.result : '';
    let passed = false;
    if (item?.passed === true) {
      passed = true;
    }
    if (result === 'passed') {
      passed = true;
    }
    items.push({
      key: `${index}-${rowLabel}`,
      questionLabel: `${rowLabel}: ${question}`,
      passed,
      reason: typeof item?.reason === 'string' ? item.reason : ''
    });
  }
  return items;
});

const stageLabel = computed(() => {
  const stage = typeof props.job?.stage === 'string' ? props.job.stage : '';
  if (stage === 'queued') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageQueued', 'Queued');
  }
  if (stage === 'initializingAi') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageInitializing', 'Initializing AI');
  }
  if (stage === 'retest-started') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageStarted', 'Preparing questions');
  }
  if (stage === 'retest-rule-try-started') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageRuleTry', 'Querying the agent');
  }
  if (stage === 'retest-agent-answer-started') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageAgentAnswer', 'Waiting for the agent answer');
  }
  if (stage === 'retest-answer-comparison-started') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageComparing', 'Comparing with the human-correct answer');
  }
  if (stage === 'retest-attempt-processed') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageAttempt', 'Comparing the agent answer');
  }
  if (stage === 'retest-row-processed') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageRow', 'Finished a question');
  }
  if (stage === 'retest-completed') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageBuilding', 'Building workbook');
  }
  if (stage === 'completed') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageCompleted', 'Completed');
  }
  if (stage === 'cancelling') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageCancelling', 'Cancelling');
  }
  if (stage === 'cancelled') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageCancelled', 'Cancelled');
  }
  if (stage === 'failed') {
    return t('aiChatAgentSetup.advanced.retestFineTuningStageFailed', 'Failed');
  }
  return t('aiChatAgentSetup.advanced.retestFineTuningStageWorking', 'Working');
});

function onBackdropClick() {
  if (props.isRunning !== true) {
    emit('close');
  }
}
</script>
